import { computed } from '@ember/object';
import { isPresent } from '@ember/utils';
import { attr } from '@ember-data/model';
import { inject as service } from '@ember/service';
import Hours from '@square/dashboard-data/models/hours';
import moment from 'moment';
import { RRule } from 'rrule';
import { momentToiCalDate } from '@square/dashboard-items-data/utils/dates';
import ItemsV3Base from '@square/dashboard-items-data/models/items-v3-base';

// Using 2017 (or any more recent year than the epoch) prevents false positives with weekly schedules if you
// have a time frame that overlaps the current date. There is a bug in old versions of the cross-platform app
// that fast forwarded the time from 1970 to the current date which caused this issue.
export const BASE_DATE = moment.utc(0).year(2017);

// Represents a time period - either a single period or a repeating period
// Used to model the conditions under which an automatic Discount applies
export default ItemsV3Base.extend({
  store: service(),
  name: attr('string'),
  dateTimeStart: attr('string'),
  recurrenceRule: attr('string'),
  duration: attr('string'),
  almanacEventId: attr('string'),
  // This is used to check if 2 time periods are for the same time in a time period
  // by combining the start time and duration into a single string.
  equivalenceIdentifier: computed('dateTimeStart', 'duration', function () {
    return `${this.dateTimeStart}-${this.duration}`;
  }),
  durationObject: computed('duration', function () {
    const duration = this.duration;
    if (isPresent(duration)) {
      return moment.duration(duration);
    }
    return null;
  }),
  recurrenceRuleObject: computed('recurrenceRule', function () {
    const rule = this.recurrenceRule;
    if (isPresent(rule)) {
      const ruleWithZ = rule.replace(/UNTIL=([^;Z]+)Z?/, 'UNTIL=$1Z');
      return RRule.fromString(ruleWithZ);
    }
    return null;
  }),
  toHours: computed('dateTimeStart', 'durationObject', 'isHolidaySchedule', 'recurrenceRuleObject.options.byweekday.firstObject', function () {
    if (this.dateTimeStart && this.durationObject && !this.isHolidaySchedule) {
      let dayOfWeekFromRRule;
      // the first day of the week is Monday with Monday = 0, Tuesday = 1, ... Sunday = 6.
      const indexOfDay = this.recurrenceRuleObject?.options?.byweekday?.firstObject;
      if (indexOfDay !== undefined) {
        // convert from a Monday based index to a Sunday based index, which Sunday = 0, Monday = 1, etc.
        dayOfWeekFromRRule = (indexOfDay + 1) % 7;
      }
      return Hours.createFromStartDateAndDuration(this.store, moment.utc(this.dateTimeStart), this.durationObject, dayOfWeekFromRRule);
    } else {
      return null;
    }
  }),
  toAlmanacEventId: computed('almanacEventId', function () {
    return this.almanacEventId ?? null;
  }),
  isWeeklySchedule: computed('recurrenceRule', function () {
    return Boolean(this.recurrenceRule);
  }),
  isHolidaySchedule: computed('almanacEventId', 'name', function () {
    return Boolean(this.almanacEventId) || Boolean(this.name);
  }),
  setValuesFromHours(hours) {
    const hoursDuration = hours.get('duration');
    const {
      hour,
      minute
    } = hoursDuration.startTime;
    const weekday = hours.dayIndex;
    const startDate = BASE_DATE.clone();
    startDate.hour(hour).minute(minute).isoWeekday(weekday);
    this.setProperties({
      dateTimeStart: momentToiCalDate(startDate),
      duration: hoursDuration.duration,
      recurrenceRule: new RRule({
        freq: RRule.WEEKLY,
        byweekday: RRule[hoursDuration.weekdayShort]
      }).toString()
    });
  },
  setValuesFromTimePeriod(timePeriod) {
    this.setProperties({
      name: timePeriod.name,
      dateTimeStart: timePeriod.dateTimeStart,
      duration: timePeriod.duration,
      recurrenceRule: timePeriod.recurrenceRule,
      almanacEventId: timePeriod.almanacEventId
    });
  }
}).reopenClass({
  _modelNameForMigration: 'time-period',
  currentUserAffectsEnabledUnitsAll: false,
  catalogType() {
    return 'TIME_PERIOD';
  },
  // TODO (nrobinson): pass in store to create record
  createFromHours(store, hours) {
    const hoursDuration = hours.get('duration');
    if (!hoursDuration) {
      return null;
    }
    const {
      hour,
      minute
    } = hoursDuration.startTime;
    const weekday = hours.dayIndex;
    const startDate = BASE_DATE.clone();
    startDate.hour(hour).minute(minute).isoWeekday(weekday);
    return store.createRecord('time-period', {
      dateTimeStart: momentToiCalDate(startDate),
      duration: hoursDuration.duration,
      recurrenceRule: new RRule({
        freq: RRule.WEEKLY,
        byweekday: RRule[hoursDuration.weekdayShort]
      }).toString()
    });
  },
  createFromAlmanacEvent(store, event) {
    const dateTimeStart = event.dates[0].startsAt;
    const duration = event.dates[0].duration;
    const almanacEventId = event.externalId;
    return store.createRecord('time-period', {
      dateTimeStart,
      duration,
      almanacEventId
    });
  },
  createFromCustomHolidayEvent(store, event) {
    const name = event.name;
    const dateTimeStart = event.dateTimeStart;
    const duration = 'PT23H59M';
    return store.createRecord('time-period', {
      name,
      dateTimeStart,
      duration
    });
  }
});